import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  ReferenceLine,
} from "recharts";

const CONDITION_COLORS = [
  "#3b82f6", "#22c55e", "#f59e0b", "#ef4444", "#8b5cf6", "#ec4899",
  "#06b6d4", "#f97316", "#14b8a6", "#a855f7", "#e11d48", "#84cc16",
];

/**
 * Butterfly/gap chart for feeling thermometers.
 * Shows support (right) and oppose (left) ratings per condition,
 * with the GAP (affective polarization) highlighted.
 */
const ThermometerGapChart = ({ supportStats, opposeStats, conditions, height = 280 }) => {
  if (!supportStats?.byCondition || !opposeStats?.byCondition) {
    return <p className="text-gray-500 text-sm">No thermometer data</p>;
  }

  const conditionLabels = {};
  conditions.forEach((c) => {
    conditionLabels[c.conditionId] = c.label || c.conditionId;
  });

  // Build chart data: one row per condition
  const chartData = conditions.map((c, i) => {
    const sup = supportStats.byCondition[c.conditionId]?.mean || 50;
    const opp = opposeStats.byCondition[c.conditionId]?.mean || 50;
    const gap = Math.abs(sup - opp);
    return {
      name: conditionLabels[c.conditionId] || c.conditionId,
      support: sup,
      oppose: -opp, // Negative for left-side rendering
      gap,
      supportRaw: sup,
      opposeRaw: opp,
      colorIndex: i,
    };
  });

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    if (!d) return null;
    return (
      <div className="bg-gray-800 border border-gray-600 rounded px-3 py-2 text-xs shadow-lg">
        <p className="text-gray-300 font-medium mb-1">{d.name}</p>
        <p className="text-blue-400">Support: {d.supportRaw?.toFixed(1)}/100</p>
        <p className="text-red-400">Oppose: {d.opposeRaw?.toFixed(1)}/100</p>
        <p className="text-yellow-400 font-bold mt-1">Gap: {d.gap?.toFixed(1)} pts</p>
      </div>
    );
  };

  return (
    <div>
      <div className="flex items-center justify-between text-xs text-gray-500 mb-1 px-2">
        <span>Oppose (cold)</span>
        <span className="text-yellow-400 font-medium">Gap = affective polarization</span>
        <span>Support (warm)</span>
      </div>
      <ResponsiveContainer width="100%" height={height}>
        <BarChart
          data={chartData}
          layout="vertical"
          margin={{ top: 5, right: 30, left: 100, bottom: 5 }}
          stackOffset="sign"
        >
          <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
          <XAxis
            type="number"
            domain={[-100, 100]}
            stroke="#9ca3af"
            tick={{ fill: "#9ca3af", fontSize: 10 }}
            tickFormatter={(v) => Math.abs(v)}
          />
          <YAxis
            type="category"
            dataKey="name"
            stroke="#9ca3af"
            tick={{ fill: "#9ca3af", fontSize: 11 }}
            width={95}
          />
          <Tooltip content={<CustomTooltip />} />
          <ReferenceLine x={0} stroke="#6b7280" strokeWidth={2} />

          {/* Oppose bars (left, negative values) */}
          <Bar dataKey="oppose" name="Oppose" stackId="therm">
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={CONDITION_COLORS[entry.colorIndex % CONDITION_COLORS.length]}
                fillOpacity={0.5}
              />
            ))}
          </Bar>

          {/* Support bars (right, positive values) */}
          <Bar dataKey="support" name="Support" stackId="therm">
            {chartData.map((entry, i) => (
              <Cell
                key={i}
                fill={CONDITION_COLORS[entry.colorIndex % CONDITION_COLORS.length]}
                fillOpacity={0.9}
              />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Gap annotations below chart */}
      <div className="flex flex-wrap gap-2 mt-2 px-2">
        {chartData.map((d, i) => (
          <div
            key={i}
            className="flex items-center gap-1.5 text-xs"
            style={{ color: CONDITION_COLORS[i % CONDITION_COLORS.length] }}
          >
            <div
              className="w-2.5 h-2.5 rounded-full"
              style={{ backgroundColor: CONDITION_COLORS[i % CONDITION_COLORS.length] }}
            />
            <span className="text-gray-400">{d.name.split(" ").slice(0, 2).join(" ")}:</span>
            <span className="font-bold">{d.gap.toFixed(0)} gap</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ThermometerGapChart;
